/*
  menu-mega.js — Méga menu, Élégance Emiratie.

  Trois enrichissements ADDITIFS du méga menu natif de Dawn. Aucun fichier Dawn
  n'est modifié : on agit au moment de l'exécution, sur le markup rendu.

  1. Les blocs — « Visuel » et « Colonne texte » sont rendus en Liquid dans des
     <template>, puis déposés ici dans la bonne colonne du bon panneau.
     Le Liquid calcule les identifiants de Dawn (`HeaderMenu-<rubrique>-<colonne>`) :
     rien n'est deviné côté JS.
  2. Les titres de colonne — un lien de menu pointant sur « # » n'est pas un lien.
     On le remplace par un intitulé (`role="heading"`), comme sur la prod.
  3. L'ouverture au survol — Dawn n'ouvre ses panneaux qu'au clic. Uniquement
     sur pointeur fin : au doigt, le clic reste la seule interaction. La
     fermeture est DIFFÉRÉE : entre le lien de rubrique et le panneau, il y a
     une zone morte (voir `activerSurvol`), et fermer sur-le-champ rendait le
     menu inatteignable à la souris.
  4. Les rubriques cliquables — le survol ayant pris en charge l'ouverture, le
     clic est libre : il mène à la page de la rubrique, comme sur la prod. La
     souris seulement ; au clavier, la rubrique reste un <summary>.
  5. Les rubriques du tiroir mobile — même manque, autre remède : faute de
     survol, on partage la rangée dans l'espace. L'intitulé devient un lien vers
     la page de la rubrique, le chevron garde le toucher qui déplie.

  Le module se ré-applique sur `shopify:section:load` : dans l'éditeur, l'en-tête
  se re-rend et emporte avec lui tout ce qu'on y a déposé.
*/

(function () {
  const ID_ANCRE_VIDE = '#';

  // Délai de grâce, en millisecondes, pour traverser la zone morte qui sépare
  // le lien de rubrique de son panneau (voir `activerSurvol`).
  const DELAI_FERMETURE = 260;

  class MenuMega extends HTMLElement {
    connectedCallback() {
      this.minuteurs = new WeakMap();
      this.appliquer = this.appliquer.bind(this);
      this.appliquer();
      document.addEventListener('shopify:section:load', this.appliquer);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.appliquer);
    }

    appliquer() {
      const entete = document.querySelector('.header');
      if (!entete) return;

      // Les blocs d'abord : ils se repèrent aux identifiants posés par Dawn sur
      // les liens de niveau 2, que l'étape suivante peut remplacer.
      this.injecterBlocs(entete);
      if (this.dataset.titres === '1') this.transformerTitres(entete);
      if (this.dataset.survol === '1') {
        this.activerSurvol(entete);
        // Rendre la rubrique cliquable sans le survol condamnerait le panneau :
        // à la souris, plus rien ne l'ouvrirait.
        if (this.dataset.rubriquesCliquables === '1') this.activerRubriquesCliquables(entete);
      }
      // Le tiroir, lui, ne dépend pas du survol : il a son propre partage de la
      // rangée (voir `activerRubriquesTiroir`).
      if (this.dataset.rubriquesCliquables === '1') this.activerRubriquesTiroir(entete);
    }

    /* ── 1. Blocs de la section ──────────────────────────────────────────── */

    injecterBlocs(entete) {
      this.querySelectorAll('template[data-mm-menu]').forEach((gabarit) => {
        const cle = gabarit.dataset.mmCle;
        if (entete.querySelector('[data-mm-cle="' + CSS.escape(cle) + '"]')) return;

        const sommaire = entete.querySelector('#' + CSS.escape(gabarit.dataset.mmMenu));
        if (!sommaire) return;

        const panneau = sommaire.closest('details.mega-menu');
        const liste = panneau && panneau.querySelector('.mega-menu__list');
        if (!liste) return;

        const contenu = gabarit.content.firstElementChild;
        if (!contenu) return;

        let colonne = null;
        if (gabarit.dataset.mmCol) {
          const intitule = entete.querySelector('#' + CSS.escape(gabarit.dataset.mmCol));
          colonne = intitule ? intitule.closest('li') : null;
        }

        // Aucune colonne d'accueil dans le menu : on en crée une en fin de panneau.
        if (!colonne) {
          colonne = document.createElement('li');
          colonne.className = 'menu-mega__col--nouvelle';
          if (gabarit.dataset.mmTitre) {
            colonne.appendChild(this.creerTitre(gabarit.dataset.mmTitre));
          }
          liste.appendChild(colonne);
        }

        colonne.appendChild(contenu.cloneNode(true));
      });
    }

    /* ── 2. Titres de colonne ────────────────────────────────────────────── */

    transformerTitres(entete) {
      const liens = entete.querySelectorAll(
        '.mega-menu__list:not(.mega-menu__list--condensed) a.mega-menu__link--level-2'
      );

      liens.forEach((lien) => {
        const cible = lien.getAttribute('href');
        if (cible !== ID_ANCRE_VIDE) return;

        const titre = this.creerTitre(lien.textContent.trim());
        // On garde l'identifiant de Dawn : c'est le point d'accroche des blocs.
        if (lien.id) titre.id = lien.id;
        lien.replaceWith(titre);
      });
    }

    creerTitre(texte) {
      const titre = document.createElement('span');
      titre.className = 'menu-mega__titre';
      titre.setAttribute('role', 'heading');
      titre.setAttribute('aria-level', '3');
      titre.textContent = texte;
      return titre;
    }

    /* ── 3. Ouverture au survol ──────────────────────────────────────────── */

    /*
      La zone morte. Chez Dawn, `.mega-menu` est en `position: static` et son
      panneau en `position: absolute; top: 100%` : le panneau se cale donc sous
      la SECTION en-tête (`.section-header`, en `position: sticky`), pas sous le
      lien de rubrique. Entre le bas du lien et le haut du panneau s'intercalent
      la hauteur restante de la rangée (le logo est plus haut que le texte) et la
      marge intérieure basse de l'en-tête — quelques dizaines de pixels qui
      n'appartiennent à AUCUN des deux. La souris qui descend vers le panneau
      quitte donc l'élément `<details>`, ce qui déclenchait `mouseleave` et
      fermait le panneau avant qu'elle ne l'atteigne — puis, le contenu d'un
      `<details>` fermé n'étant plus rendu, plus rien ne pouvait le rouvrir.

      D'où la fermeture différée : on laisse au pointeur le temps de traverser.
      Il rentre alors dans le panneau, qui est un descendant du `<details>` —
      `mouseenter` se redéclenche et annule le minuteur.
    */
    pointeurFin() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    activerSurvol(entete) {
      if (!this.pointeurFin()) return;

      entete.querySelectorAll('details.mega-menu').forEach((panneau) => {
        if (panneau.dataset.mmSurvol === '1') return;
        panneau.dataset.mmSurvol = '1';

        panneau.addEventListener('mouseenter', () => {
          this.annulerFermeture(panneau);
          this.fermerAutres(entete, panneau);
          if (panneau.open) return;
          panneau.open = true;
          const sommaire = panneau.querySelector('summary');
          if (sommaire) sommaire.setAttribute('aria-expanded', 'true');
        });

        panneau.addEventListener('mouseleave', () => {
          // Le clavier a la priorité : on ne referme pas sous les doigts de
          // quelqu'un qui navigue au Tab et dont la souris passe par là.
          if (panneau.contains(document.activeElement)) return;
          this.programmerFermeture(panneau);
        });
      });
    }

    programmerFermeture(panneau) {
      this.annulerFermeture(panneau);
      this.minuteurs.set(
        panneau,
        setTimeout(() => {
          this.minuteurs.delete(panneau);
          if (panneau.contains(document.activeElement)) return;
          // Dernier filet : le pointeur a pu revenir sans repasser par
          // `mouseenter` (retour sur le lien de rubrique, par exemple).
          if (panneau.matches(':hover')) return;
          this.fermer(panneau);
        }, DELAI_FERMETURE)
      );
    }

    annulerFermeture(panneau) {
      const minuteur = this.minuteurs.get(panneau);
      if (minuteur === undefined) return;
      clearTimeout(minuteur);
      this.minuteurs.delete(panneau);
    }

    /* ── 4. Rubriques de premier niveau cliquables ───────────────────────── */

    activerRubriquesCliquables(entete) {
      if (!this.pointeurFin()) return;

      const liens = this.lireLiens();

      entete.querySelectorAll('details.mega-menu > summary').forEach((sommaire) => {
        if (sommaire.dataset.mmLien) return;

        const url = liens[sommaire.id];
        // « # » est une rubrique-conteneur : elle n'a pas de page où aller.
        if (!url || url === ID_ANCRE_VIDE) return;

        sommaire.dataset.mmLien = url;

        sommaire.addEventListener('click', (event) => {
          // `detail === 0` = activation au clavier (Entrée/Espace). On laisse
          // alors <summary> faire son travail : sinon le panneau deviendrait
          // inatteignable sans souris.
          if (event.detail === 0) return;
          event.preventDefault();
          window.location.href = url;
        });
      });
    }

    /* ── 5. Rubriques du tiroir mobile ───────────────────────────────────── */

    /*
      Même manque qu'au bureau, mais on ne peut pas le combler de la même façon.
      Le tiroir de Dawn rend lui aussi les rubriques à sous-menu en <summary> :
      « PARFUMS SIGNATURE » déplie, il ne mène nulle part. Au bureau, le survol
      ayant pris l'ouverture en charge, le clic était libre. Au doigt, il n'y a
      pas de survol : détourner le toucher de la rangée entière rendrait le
      sous-menu inatteignable.

      On partage donc la rangée dans l'espace. L'intitulé devient un vrai <a>
      qui mène à la page de la rubrique ; le chevron, que Dawn cale en absolu à
      droite, garde le toucher qui déplie. Le lien s'étend sur toute la hauteur
      de la rangée mais s'arrête avant le chevron — c'est `menu-mega.css` qui
      trace la frontière, en une seule valeur.

      Le clavier n'est pas touché : Entrée sur le <summary> continue de déplier,
      et le <a> reste atteignable au Tab comme n'importe quel lien.
    */
    activerRubriquesTiroir(entete) {
      const liens = this.lireLiens('tiroir');

      entete.querySelectorAll('.menu-drawer__navigation').forEach((navigation) => {
        navigation
          .querySelectorAll(':scope > .menu-drawer__menu > li > details > summary')
          .forEach((sommaire) => {
            if (sommaire.dataset.mmTiroir) return;

            const url = liens[sommaire.id];
            // « # » est une rubrique-conteneur : elle n'a pas de page où aller.
            if (!url || url === ID_ANCRE_VIDE) return;

            // L'intitulé est un nœud de texte nu chez Dawn, sans élément à saisir :
            // on l'enveloppe. Le premier nœud de texte non vide est le libellé ;
            // ce qui suit (les deux `.svg-wrapper` des chevrons) ne bouge pas.
            const libelle = Array.from(sommaire.childNodes).find(
              (noeud) => noeud.nodeType === Node.TEXT_NODE && noeud.textContent.trim() !== ''
            );
            if (!libelle) return;

            const lien = document.createElement('a');
            lien.className = 'menu-mega__tiroir-lien';
            lien.href = url;
            lien.textContent = libelle.textContent.trim();
            libelle.replaceWith(lien);

            sommaire.dataset.mmTiroir = url;
          });

        if (navigation.dataset.mmTiroir === '1') return;
        navigation.dataset.mmTiroir = '1';
        navigation.addEventListener('click', this.surClicTiroir, true);
      });
    }

    /*
      En CAPTURE, et sur la navigation plutôt que sur le <summary>. Dawn pose son
      propre gestionnaire de clic sur chaque <summary> (`menu-drawer.js`), et il
      s'enregistre avant le nôtre : `menu-drawer.js` est chargé par la section
      En-tête, qui précède celle-ci dans le groupe. Un gestionnaire posé sur le
      <summary> — même avec `stopImmediatePropagation` — passerait donc après le
      sien, et le sous-menu aurait le temps de glisser à l'écran avant que la
      page ne change. Capté un cran plus haut, le clic n'atteint jamais le
      <summary> : ni Dawn, ni le dépliement natif du <details>.
    */
    surClicTiroir(event) {
      const lien = event.target.closest('.menu-mega__tiroir-lien');
      if (!lien) return;
      // Clic du milieu, Cmd/Ctrl-clic : on laisse le navigateur ouvrir son
      // onglet, et le sous-menu se déplier — c'est sans gravité.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      window.location.href = lien.href;
    }

    /* Les URL des rubriques, rendues en JSON par la section. `champ` dit sur
       quel identifiant indexer : celui du menu de bureau ou celui du tiroir. */
    lireLiens(champ = 'id') {
      const source = this.querySelector('script[data-mm-liens]');
      if (!source) return {};
      try {
        const entrees = JSON.parse(source.textContent);
        return Object.fromEntries(entrees.map((lien) => [lien[champ], lien.url]));
      } catch (erreur) {
        return {};
      }
    }

    fermerAutres(entete, garde) {
      entete.querySelectorAll('details.mega-menu[open]').forEach((autre) => {
        if (autre !== garde) this.fermer(autre);
      });
    }

    fermer(panneau) {
      this.annulerFermeture(panneau);
      // `header-menu` (Dawn) sait se fermer proprement : `open` + `aria-expanded`.
      const disclosure = panneau.closest('header-menu');
      if (disclosure && typeof disclosure.close === 'function') {
        disclosure.close();
        return;
      }
      panneau.removeAttribute('open');
      const sommaire = panneau.querySelector('summary');
      if (sommaire) sommaire.setAttribute('aria-expanded', 'false');
    }
  }

  if (!customElements.get('menu-mega')) customElements.define('menu-mega', MenuMega);
})();
